import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Seo } from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import { motion } from 'framer-motion';
import {
  Mail,
  MapPin,
  MessageCircle,
  Clock,
  ExternalLink,
  Building2,
  Phone,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { configApi } from '@/api/configApi';

/* ------------------------------------------------------------------ */
/*  Contact info card                                                   */
/* ------------------------------------------------------------------ */
interface ContactCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  hint?: string;
  href: string;
  delay?: number;
}

function ContactCard({ icon, title, value, hint, href, delay = 0 }: ContactCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: 'easeOut' }}
    >
      <a href={href} target="_blank" rel="noopener noreferrer">
        <Card className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary/20">
          <CardContent className="flex items-start gap-4">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
              {icon}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {title}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground break-all">{value}</p>
              {hint && (
                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <ExternalLink className="size-3" />
                  {hint}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </a>
    </motion.div>
  );
}

/* ================================================================== */
/*  CONTACT PAGE                                                        */
/* ================================================================== */
export default function Contact() {
  const { contact, business_hours } = useSiteConfig();
  const location = useLocation();
  const [centres, setCentres] = useState<
    {
      city: string;
      centres: { name: string; address: string; contact_number: string; google_maps_link?: string }[];
    }[]
  >([]);

  useEffect(() => {
    configApi
      .getCentres()
      .then((res) => {
        if (res.data?.cities) setCentres(res.data.cities);
      })
      .catch(() => {});
  }, []);

  /* Scroll to hash (e.g. #centres) once centres are loaded */
  useEffect(() => {
    if (location.hash && centres.length > 0) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [location.hash, centres]);

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`;

  // The configured WhatsApp value may be a full wa.me URL, a bare number, or
  // empty - normalise it into a working https://wa.me/<digits> link. A bare
  // 10-digit number gets the India country code so WhatsApp can resolve it.
  const whatsappUrl = (() => {
    const raw = (contact.whatsapp || '').trim();
    if (/^https?:\/\//i.test(raw)) return raw;
    let digits = (raw || contact.phone || '').replace(/\D/g, '');
    if (digits.length === 10) digits = `91${digits}`;
    return digits ? `https://wa.me/${digits}` : '';
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="mx-auto max-w-2xl px-4 py-10 sm:px-6"
    >
      <Seo
        title="Contact Recovry | Verified Physiotherapy & Rehab Platform"
        description="Get in touch with Recovry. Reach us via WhatsApp, email, or contact our team for support, partnerships, or to list your physiotherapy centre."
        canonical="/contact"
        schema={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Contact', path: '/contact' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Recovry',
            url: 'https://www.recovry.in/contact',
            description: 'Contact Recovry for support, partnerships and centre onboarding.',
            mainEntity: {
              '@type': 'Organization',
              name: 'Recovry',
              email: contact.email,
              telephone: contact.phone,
              address: {
                '@type': 'PostalAddress',
                streetAddress: contact.address,
                addressCountry: 'IN',
              },
            },
          },
        ]}
      />
      {/* Page Header */}
      <div className="mb-10 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
        >
          Contact Us
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-2 text-muted-foreground"
        >
          We're here to help you relax
        </motion.p>
      </div>

      {/* WhatsApp - Primary CTA (prominent at top) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
        className="mb-6"
      >
        <Button
          asChild
          className="w-full gap-2.5 py-6 text-base font-semibold text-white shadow-lg transition-all hover:shadow-xl"
          style={{ backgroundColor: '#25D366' }}
          size="lg"
        >
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="size-5" />
            Chat with Us on WhatsApp
          </a>
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Preferred - get a faster response via WhatsApp
        </p>
      </motion.div>

      {/* Contact Cards */}
      <div className="space-y-4">
        {/* Phone - links to WhatsApp */}
        <ContactCard
          icon={<MessageCircle className="size-5" />}
          title="Phone / WhatsApp"
          value={contact.phone}
          hint="Tap to chat on WhatsApp"
          href={whatsappUrl}
          delay={0.15}
        />

        <ContactCard
          icon={<Mail className="size-5" />}
          title="Email"
          value={contact.email}
          hint="Send us an email"
          href={`mailto:${contact.email}`}
          delay={0.2}
        />

        <ContactCard
          icon={<MapPin className="size-5" />}
          title="Address"
          value={contact.address}
          hint="Open in Google Maps"
          href={mapsUrl}
          delay={0.25}
        />
      </div>

      {/* Business Hours */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.35, ease: 'easeOut' }}
        className="mt-8"
      >
        <Card>
          <CardContent>
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10">
                <Clock className="size-4.5 text-primary" />
              </div>
              <h2 className="text-base font-semibold text-foreground">Business Hours</h2>
            </div>

            <div className="overflow-hidden rounded-lg border">
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Days
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Hours
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {business_hours.map((row, i) => (
                    <tr key={row.days} className={i < business_hours.length - 1 ? 'border-b' : ''}>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{row.days}</td>
                      <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                        {row.time}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

    </motion.div>
  );
}
